<template>
  <div class="permissions-groups-container">
    <div
      v-for="groupName in Object.keys(permissionsByGroup)"
      :key="groupName"
      class="w-full rounded-[12px] bg-white border border-neutral-200 cursor-pointer mb-4 last:mb-0 general overflow-hidden relative"
      :class="{ 'mb-0': permissionGroupActive === groupName }"
    >
      <div
        class="collapse-title flex flex-row items-center gap-3 px-5 py-4 hover:bg-neutral-50"
        @click="togglePermissionGroups(groupName)"
      >
        <span
          class="material-symbols-outlined transition-all text-gray-500"
          :class="
            permissionGroupActive === groupName ? 'rotate-90' : 'rtl:rotate-180'
          "
        >
          keyboard_arrow_right
        </span>
        <div class="roles-desc flex-1">
          <div class="flex flex-row items-center mb-1 gap-2">
            <div class="font-semibold text-gray-800">
              {{ $t(`ROLES.GENERAL_PERMISSIONS_BY_GROUP.${groupName}.TITLE`) }}
            </div>
            <span class="text-gray-500">
              {{ permissionsCountByGroup[groupName].selected }}/{{
                permissionsCountByGroup[groupName].total
              }}
            </span>
          </div>
          <div class="text-xs/[18px]">
            {{ $t(`ROLES.GENERAL_PERMISSIONS_BY_GROUP.${groupName}.DESC`) }}
          </div>
        </div>
        <label
          class="flex flex-row items-center"
          :class="{ 'pointer-events-none': readonly }"
          @click.stop="selectAllBy(groupName, $event)"
        >
          <input
            type="checkbox"
            :checked="selectAllOptions[groupName].all"
            :indeterminate.prop="selectAllOptions[groupName].some"
            class="semantic-checkbox !m-0 cursor-pointer !mr-2 rtl:!ml-2 rtl:!mr-0"
          />
          <span class="text-gray-600 text-sm">
            {{ $t(`ROLES.SELECT_ALL`) }}
          </span>
        </label>
      </div>
      <div
        class="collapse-content"
        :class="[
          { collapse: permissionGroupActive !== groupName },
          { 'pointer-events-none': readonly },
        ]"
      >
        <table class="woot-table">
          <thead>
            <tr>
              <th class="rtl:text-right">
                {{ $t('ROLES.TABLE.HEADER.MODULE') }}
              </th>
              <th class="permission-header-cell">
                {{ $t('ROLES.TABLE.HEADER.READ') }}
              </th>
              <th class="permission-header-cell">
                {{ $t('ROLES.TABLE.HEADER.CREATE') }}
              </th>
              <th class="permission-header-cell">
                {{ $t('ROLES.TABLE.HEADER.UPDATE') }}
              </th>
              <th class="permission-header-cell">
                {{ $t('ROLES.TABLE.HEADER.DELETE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="permission_key in Object.keys(
                permissionsByGroup[groupName]
              )"
              :key="permission_key"
            >
              <td>
                <div>
                  <div class="text-sm text-gray-800 mb-1">
                    {{ permissionName(permission_key) }}
                  </div>
                  <div class="text-xs/[18px] text-gray-600">
                    {{ permissionDesc(permission_key) }}
                  </div>
                </div>
              </td>
              <td
                v-for="action in ['read', 'create', 'update', 'delete']"
                :key="action"
                class="!p-0 align-middle"
              >
                <label>
                  <input
                    v-if="initialPermissions[permission_key][action] !== 'no'"
                    :checked="permissions[permission_key][action]"
                    type="checkbox"
                    class="semantic-checkbox !mr-0"
                    @click.stop
                    @change="
                      togglePermission(
                        permission_key,
                        action,
                        $event.target.checked
                      )
                    "
                  />
                  <input
                    v-else
                    type="checkbox"
                    class="semantic-checkbox !mr-0"
                    disabled
                  />
                </label>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import {
  GENERAL_PERMISSIONS_GROUPS,
  GENERAL_PERMISSIONS,
  FEATURE_GATED_PERMISSIONS,
} from './constants';

export default {
  props: {
    value: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      permissionGroupActive: null,
      initialPermissions: GENERAL_PERMISSIONS,
      permissions: this.value,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    // d99d Drops the permissions belonging to a feature this account does not have, along with any
    // group left empty by that.
    permissionsByGroup() {
      return Object.fromEntries(
        Object.entries(GENERAL_PERMISSIONS_GROUPS)
          .map(([groupName, permissionsInGroup]) => [
            groupName,
            Object.fromEntries(
              Object.entries(permissionsInGroup).filter(([permissionName]) =>
                this.isPermissionAvailable(permissionName)
              )
            ),
          ])
          .filter(
            ([, permissionsInGroup]) =>
              Object.keys(permissionsInGroup).length > 0
          )
      );
    },
    // The individual checkboxes that actually render, used for the "selected of total" counts.
    eligiblePermissions() {
      return Object.entries(this.permissionsByGroup).flatMap(
        ([groupName, permissionsInGroupAsObj]) =>
          Object.entries(permissionsInGroupAsObj).flatMap(
            ([permissionName, initialPermissionAttrs]) =>
              Object.entries(initialPermissionAttrs)
                .filter(([, defaultVal]) => defaultVal !== 'no')
                .map(([action]) => ({ groupName, permissionName, action }))
          )
      );
    },
    selectAllOptions() {
      return Object.fromEntries(
        Object.entries(this.permissionsCountByGroup).map(
          ([groupName, { total, selected }]) => [
            groupName,
            {
              all: total > 0 && selected === total,
              some: selected > 0 && selected < total,
            },
          ]
        )
      );
    },
    permissionsCountByGroup() {
      const counts = Object.fromEntries(
        Object.keys(this.permissionsByGroup).map(g => [
          g,
          { total: 0, selected: 0 },
        ])
      );
      this.eligiblePermissions.forEach(
        ({ groupName, permissionName, action }) => {
          counts[groupName].total += 1;
          if (this.permissions[permissionName][action])
            counts[groupName].selected += 1;
        }
      );
      return counts;
    },
  },
  methods: {
    // d99d A feature-gated permission only exists for accounts its feature is enabled on.
    isPermissionAvailable(permissionName) {
      const feature = FEATURE_GATED_PERMISSIONS[permissionName];
      if (!feature) {
        return true;
      }
      return this.isFeatureEnabledonAccount(this.accountId, feature);
    },
    togglePermissionGroups(groupName) {
      if (groupName === this.permissionGroupActive)
        this.permissionGroupActive = null;
      else this.permissionGroupActive = groupName;
    },
    permissionName(resource) {
      return this.$t(
        `ROLES.GENERAL_PERMISSIONS.${resource.replace(/ /g, '_').toUpperCase()}`
      );
    },
    permissionDesc(resource) {
      return this.$t(
        `ROLES.GENERAL_PERMISSIONS.${resource
          .replace(/ /g, '_')
          .toUpperCase()}_DESC`
      );
    },
    // Forms rows: create/update/delete require read on the same row, and Forms Responses read
    // requires read on Forms.
    togglePermission(permissionKey, action, checked) {
      this.setPermission(permissionKey, action, checked);

      const dependsOnOwnRead = ['Forms', 'IntegrationForms'].includes(
        permissionKey
      );

      if (checked && dependsOnOwnRead && action !== 'read') {
        this.setPermission(permissionKey, 'read', true);
      } else if (checked && permissionKey === 'Forms Responses') {
        this.setPermission('Forms', 'read', true);
      } else if (!checked && action === 'read') {
        if (dependsOnOwnRead) {
          ['create', 'update', 'delete'].forEach(dependentAction =>
            this.setPermission(permissionKey, dependentAction, false)
          );
        }
        if (permissionKey === 'Forms') {
          this.setPermission('Forms Responses', 'read', false);
        }
      }
    },
    setPermission(permissionKey, action, value) {
      if (this.permissions[permissionKey][action] === 'no') return;
      this.$set(this.permissions[permissionKey], action, value);
    },
    selectAllBy(groupName, event) {
      Object.keys(this.permissionsByGroup[groupName]).forEach(
        permission_key => {
          ['read', 'create', 'update', 'delete'].forEach(action => {
            if (this.permissions[permission_key][action] !== 'no') {
              this.permissions[permission_key][action] =
                event.srcElement.checked;
            }
          });
        }
      );
    },
  },
};
</script>
<style lang="scss" scoped>
.permissions-groups-container {
  @apply w-full;
}
.woot-table {
  border-collapse: collapse;
  margin-bottom: 0px;
  thead {
    @apply bg-neutral-200 border-neutral-200;
    th {
      @apply text-[#717171] text-sm uppercase border-0 px-3 py-2;
      letter-spacing: normal;
    }
    .permission-header-cell {
      @apply w-[138px] text-center;
    }
  }

  tbody {
    td {
      @apply px-3 py-2 border-b border-gray-100;
      transition: background-color 0.3s ease;

      &.align-middle {
        height: 1px;

        label {
          @apply flex items-center justify-center w-full rounded cursor-pointer;
          height: 100%;
        }

        &:hover {
          @apply bg-gray-50;
        }

        &:has(input:checked) {
          @apply bg-[#F0FDF4];
        }

        &:has(input:disabled) {
          @apply bg-gray-50 cursor-not-allowed;

          label {
            @apply cursor-not-allowed;
          }

          input {
            background: #e5e5e5;
          }
        }
      }
    }
    tr:last-child {
      border-bottom: none;
      td {
        border-bottom: none;
      }
    }
  }
}
</style>
